/**
 * Mock Query Handler for Natural Language Query Interface
 *
 * This simulates AI query processing for prototype UX validation.
 * TODO: Replace with actual API call to serverless endpoint
 *
 * @param {string} query - Natural language question from user
 * @param {Object} currentLocation - Current location selection {district, subcounty, parish, village}
 * @param {string} category - Active category (health, education, etc.)
 * @param {Array} facilities - Available facilities data
 * @returns {Promise<Object>} Query response with type, text, and data
 */

// Query pattern definitions for pattern matching
const patterns = {
  // Teacher-related queries
  needsTeachers:
    /need.*teacher|shortage.*teacher|teacher.*ratio|pupil.*teacher/i,

  // Infrastructure queries
  lackingInfrastructure:
    /without.*electricity|no.*electricity|lacking.*toilet|without.*water|no.*water/i,
  lackingElectricity: /without.*electricity|no.*electricity/i,
  lackingWater: /without.*water|no.*water/i,

  // Service queries (health-specific)
  immunization: /immunization|vaccination|vaccine/i,
  maternalHealth: /maternal.*health|pregnancy|delivery|midwife/i,
  hivTbCare: /hiv|tb|tuberculosis|aids/i,
  laboratory: /laboratory|lab.*service|testing/i,

  // Comparison queries
  comparison: /compare|versus|vs|difference between|contrast/i,

  // Ranking queries
  ranking: /best|worst|highest|lowest|rank|most|least|top|bottom/i,

  // Statistical queries
  average: /average|mean/i,
  count: /how many|number of|count/i,

  // Ownership queries
  ownership: /government|private|ngo|ownership|run by/i,
}

/**
 * Simulate API delay for realistic UX
 */
async function simulateDelay(ms = 1500) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Get location name from location object
 */
function getLocationName(location, locations) {
  if (!location || !locations) return 'the selected area'

  // Find location by ID
  const findLoc = id => {
    if (!id) return null
    const prefix = id.split('-')[0]

    if (prefix === 'd') {
      return locations.districts?.find(d => d.id === id)
    }

    for (const district of locations.districts || []) {
      if (prefix === 's') {
        const subcounty = district.subcounties?.find(s => s.id === id)
        if (subcounty) return subcounty
      }

      for (const subcounty of district.subcounties || []) {
        if (prefix === 'p') {
          const parish = subcounty.parishes?.find(p => p.id === id)
          if (parish) return parish
        }

        for (const parish of subcounty.parishes || []) {
          if (prefix === 'v') {
            const village = parish.villages?.find(v => v.id === id)
            if (village) return village
          }
        }
      }
    }
    return null
  }

  // Get the most specific location selected
  if (location.village) {
    const loc = findLoc(location.village)
    return loc?.name || 'the selected village'
  }
  if (location.parish) {
    const loc = findLoc(location.parish)
    return loc?.name || 'the selected parish'
  }
  if (location.subcounty) {
    const loc = findLoc(location.subcounty)
    return loc?.name || 'the selected subcounty'
  }
  if (location.district) {
    const loc = findLoc(location.district)
    return loc?.name || 'the selected district'
  }

  return 'the selected area'
}

/**
 * Filter facilities by location
 */
function filterByLocation(facilities, currentLocation) {
  if (!currentLocation.district) return facilities

  return facilities.filter(facility => {
    // Match location codes based on selection depth
    if (currentLocation.village) {
      return (
        facility.locationCode === currentLocation.village ||
        facility.location_code === currentLocation.village
      )
    }
    if (currentLocation.parish) {
      return (
        facility.locationCode?.startsWith(currentLocation.parish) ||
        facility.location_code?.startsWith(currentLocation.parish)
      )
    }
    if (currentLocation.subcounty) {
      return (
        facility.locationCode?.startsWith(currentLocation.subcounty) ||
        facility.location_code?.startsWith(currentLocation.subcounty)
      )
    }
    if (currentLocation.district) {
      return (
        facility.locationCode?.startsWith(currentLocation.district) ||
        facility.location_code?.startsWith(currentLocation.district)
      )
    }
    return true
  })
}

/**
 * Process immunization query (health-specific)
 */
function processImmunizationQuery(query, facilities, locationName) {
  const facilitiesWithImmunization = facilities.filter(
    f => f.hasImmunization === 'Yes' || f.has_immunization === 'Yes'
  )

  if (facilitiesWithImmunization.length === 0) {
    return {
      type: 'answer',
      text: `No facilities offering immunization services were found in ${locationName}.`,
      data: {
        count: 0,
        location: locationName,
      },
    }
  }

  return {
    type: 'filter',
    text: `Found ${facilitiesWithImmunization.length} facilities offering immunization services in ${locationName}`,
    filters: {
      services: {
        hasImmunization: 'Yes',
      },
    },
    resultCount: facilitiesWithImmunization.length,
    suggestedView: 'grid',
  }
}

/**
 * Process infrastructure query (electricity, water, etc.)
 */
function processInfrastructureQuery(query, facilities, locationName) {
  let filterKey = null
  let filterLabel = ''

  if (patterns.lackingElectricity.test(query)) {
    filterKey = 'hasBackupPower'
    filterLabel = 'backup power'
  } else if (patterns.lackingWater.test(query)) {
    filterKey = 'waterSource'
    filterLabel = 'water source'
  }

  if (!filterKey) {
    return {
      type: 'answer',
      text: `I can help you find facilities lacking infrastructure. Try asking about electricity, water, or specific amenities.`,
      data: {},
    }
  }

  const lackingFacilities = facilities.filter(
    f => !f[filterKey] || f[filterKey] === 'No' || f[filterKey] === ''
  )

  return {
    type: 'filter',
    text: `Found ${lackingFacilities.length} facilities without ${filterLabel} in ${locationName}`,
    filters: {
      infrastructure: {
        [filterKey]: 'No',
      },
    },
    resultCount: lackingFacilities.length,
    suggestedView: 'grid',
  }
}

/**
 * Process ranking/comparison query
 */
function processRankingQuery(
  _query,
  facilities,
  locationName,
  _locations,
  _currentLocation
) {
  // Group facilities by subcounty and count
  const subcountyStats = {}

  facilities.forEach(facility => {
    // Extract subcounty from location code
    let subcountyId = null

    if (facility.locationCode || facility.location_code) {
      const code = facility.locationCode || facility.location_code
      // Parse subcounty ID from location code (format: D##S##...)
      const match = code.match(/^(D\d+S\d+)/)
      if (match) {
        subcountyId = match[1]
      }
    }

    if (subcountyId) {
      if (!subcountyStats[subcountyId]) {
        subcountyStats[subcountyId] = {
          count: 0,
          name: facility.subcounty || subcountyId,
        }
      }
      subcountyStats[subcountyId].count++
    }
  })

  // Convert to array and sort
  const rankings = Object.entries(subcountyStats)
    .map(([_id, stats]) => ({
      location: stats.name,
      value: stats.count,
      metric: 'facilities',
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10) // Top 10

  if (rankings.length === 0) {
    return {
      type: 'answer',
      text: `Not enough data to create rankings for ${locationName}.`,
      data: {},
    }
  }

  return {
    type: 'comparison',
    text: `Facility distribution across subcounties in ${locationName}:`,
    rankings,
    suggestedView: 'comparison',
  }
}

/**
 * Process count query
 */
function processCountQuery(query, facilities, locationName) {
  const total = facilities.length

  // Try to determine what's being counted
  if (patterns.immunization.test(query)) {
    const count = facilities.filter(
      f => f.hasImmunization === 'Yes' || f.has_immunization === 'Yes'
    ).length

    return {
      type: 'answer',
      text: `There are ${count} facilities offering immunization services out of ${total} total facilities in ${locationName}.`,
      data: {
        count,
        total,
        percentage: Math.round((count / total) * 100),
        location: locationName,
      },
    }
  }

  // Default count
  return {
    type: 'answer',
    text: `There are ${total} health facilities in ${locationName}.`,
    data: {
      count: total,
      location: locationName,
    },
  }
}

/**
 * Process ownership query
 */
function processOwnershipQuery(query, facilities, locationName) {
  const ownershipStats = {}

  facilities.forEach(facility => {
    const ownership = facility.ownership || 'Unknown'
    ownershipStats[ownership] = (ownershipStats[ownership] || 0) + 1
  })

  const rankings = Object.entries(ownershipStats)
    .map(([ownership, count]) => ({
      location: ownership,
      value: count,
      metric: 'facilities',
    }))
    .sort((a, b) => b.value - a.value)

  const total = facilities.length
  const topOwnership = rankings[0]

  return {
    type: 'comparison',
    text: `Facility ownership breakdown in ${locationName}: ${topOwnership.location} (${topOwnership.value} facilities, ${Math.round((topOwnership.value / total) * 100)}%)`,
    rankings,
    suggestedView: 'charts',
  }
}

/**
 * Main query processor
 */
export async function processQuery(
  query,
  currentLocation,
  category,
  facilities,
  locations
) {
  // Simulate API delay
  await simulateDelay()

  // Validate inputs
  if (!query || query.trim().length === 0) {
    return {
      type: 'error',
      text: 'Please enter a question to search.',
      data: {},
    }
  }

  if (!facilities || facilities.length === 0) {
    return {
      type: 'error',
      text: 'No facility data available. Please select a category and location.',
      data: {},
    }
  }

  // Get location-filtered facilities
  const locationFacilities = filterByLocation(facilities, currentLocation)
  const locationName = getLocationName(currentLocation, locations)

  if (locationFacilities.length === 0) {
    return {
      type: 'answer',
      text: `No facilities found in ${locationName}. Try selecting a different location.`,
      data: {
        location: locationName,
      },
    }
  }

  // Pattern matching for different query types

  // Health-specific: Immunization
  if (patterns.immunization.test(query)) {
    return processImmunizationQuery(query, locationFacilities, locationName)
  }

  // Maternal health
  if (patterns.maternalHealth.test(query)) {
    const facilitiesWithMaternal = locationFacilities.filter(
      f => f.hasMaternalHealth === 'Yes' || f.has_maternal_health === 'Yes'
    )

    return {
      type: 'filter',
      text: `Found ${facilitiesWithMaternal.length} facilities offering maternal health services in ${locationName}`,
      filters: {
        services: {
          hasMaternalHealth: 'Yes',
        },
      },
      resultCount: facilitiesWithMaternal.length,
      suggestedView: 'grid',
    }
  }

  // HIV/TB care
  if (patterns.hivTbCare.test(query)) {
    const facilitiesWithHivTb = locationFacilities.filter(
      f => f.hasHivTbCare === 'Yes' || f.has_hiv_tb_care === 'Yes'
    )

    return {
      type: 'filter',
      text: `Found ${facilitiesWithHivTb.length} facilities offering HIV/TB care in ${locationName}`,
      filters: {
        services: {
          hasHivTbCare: 'Yes',
        },
      },
      resultCount: facilitiesWithHivTb.length,
      suggestedView: 'grid',
    }
  }

  // Laboratory
  if (patterns.laboratory.test(query)) {
    const facilitiesWithLab = locationFacilities.filter(
      f => f.hasLaboratory === 'Yes' || f.has_laboratory === 'Yes'
    )

    return {
      type: 'filter',
      text: `Found ${facilitiesWithLab.length} facilities with laboratory services in ${locationName}`,
      filters: {
        infrastructure: {
          hasLaboratory: 'Yes',
        },
      },
      resultCount: facilitiesWithLab.length,
      suggestedView: 'grid',
    }
  }

  // Infrastructure queries
  if (patterns.lackingInfrastructure.test(query)) {
    return processInfrastructureQuery(query, locationFacilities, locationName)
  }

  // Ranking queries
  if (patterns.ranking.test(query)) {
    return processRankingQuery(
      query,
      locationFacilities,
      locationName,
      locations,
      currentLocation
    )
  }

  // Count queries
  if (patterns.count.test(query)) {
    return processCountQuery(query, locationFacilities, locationName)
  }

  // Ownership queries
  if (patterns.ownership.test(query)) {
    return processOwnershipQuery(query, locationFacilities, locationName)
  }

  // Default fallback
  return {
    type: 'answer',
    text: `I found ${locationFacilities.length} facilities in ${locationName}. Try asking about specific services (immunization, maternal health, laboratory) or infrastructure (electricity, water).`,
    data: {
      count: locationFacilities.length,
      location: locationName,
    },
    suggestions: [
      'Which facilities offer immunization services?',
      'Show me facilities without electricity',
      'How many facilities have laboratory services?',
      'Compare government and private facilities',
    ],
  }
}

/**
 * Get example queries based on category
 */
export function getExampleQueries(category = 'health') {
  const healthExamples = [
    'Which facilities offer immunization services?',
    'Show me facilities without backup power',
    'How many facilities have maternal health services?',
    'Which subcounty has the most health facilities?',
    'Compare government and private facilities',
  ]

  const educationExamples = [
    'Which subcounty needs more teachers?',
    'Show me schools without electricity',
    "What's the average pupil-teacher ratio?",
    'Which schools have the best infrastructure?',
    'Compare government and private schools',
  ]

  return category === 'health' ? healthExamples : educationExamples
}
